//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import FocusTrap from 'focus-trap-react';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
// import clientStyles from '@style/common/client.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Modal, Button, ButtonStyle, Input } from '@cmp/common';
//------------------------------------------------------------------------------
// Classes ---------------------------------------------------------------------
import FormValidator from '@classes/formValidator';
//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import { withReferral, sendReferral, clearReferral } from '@helpers/referrals';
import { responseFormatter } from '@helpers/formatter';
import {
  isPetNameValid,
  isNameValid,
  isEmailValid,
  isPhoneNumberValid,
} from '@helpers/validation';
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import { keyValidate } from '@helpers/validation';
import { handleGtmEvent } from '@helpers/gtm';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class Patient extends React.Component {
  static contextType = StoreConfigContext;

  constructor(props) {
    super(props);
    this.state = {
      petName: '',
      name: '',
      email: '',
      phoneNumber: '',
      loading: false,
      errorMessage: null,
      countryCode: '',
    };
    this.onSendClick = this.onSendClick.bind(this);
    this.setupValidator();
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  componentDidMount() {
    this.setupData();
    this.mountTrap();
  }

  mountTrap() {
    this.setState({ activeTrap: true });
  }

  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  setupData() {
    const { location: { state } = {} } = this.props;

    const { fromReferral } = state || {};
    if (!fromReferral) return;

    const {
      consumerEmail: email = '',
      consumerName: name = '',
      consumerPhone: phoneNumber = '',
    } = fromReferral;
    this.setState({
      email: email || '',
      petName: fromReferral.refDetails?.petName,
      name,
      phoneNumber: phoneNumber || '',
    });
  }

  setupValidator() {
    const { storeConfig = {} } = this.context || {};
    const { countryCode = '' } = storeConfig;

    // to-do: create exclusivity rules in FormValidator
    const items = [
      {
        key: 'petName',
        defaultValue: '',
        method: (string) => isPetNameValid(string),
        validWhen: true,
      },
      {
        key: 'name',
        defaultValue: '',
        method: (string) => isNameValid(string),
        validWhen: true,
      },
      {
        key: 'email',
        defaultValue: '',
        method: (string, data) => {
          if (!data.phoneNumber || !data.email) {
            return (
              isEmailValid(string) ||
              isPhoneNumberValid(data.phoneNumber, countryCode)
            );
          }

          return (
            isEmailValid(string) &&
            isPhoneNumberValid(data.phoneNumber, countryCode)
          );
        },
        validWhen: true,
      },
      {
        key: 'phoneNumber',
        defaultValue: '',
        method: (string, data) => {
          if (!data.phoneNumber || !data.email) {
            return (
              isPhoneNumberValid(string, countryCode) ||
              isEmailValid(data.email)
            );
          }

          return (
            isPhoneNumberValid(string, countryCode) && isEmailValid(data.email)
          );
        },
        validWhen: true,
      },
    ];

    this.validator = new FormValidator(items);
  }

  headerComponent() {
    const { t } = this.props;
    return (
      <div className={styles.Header}>
        <h2 className={styles.Title}>{t('dashboard_ParentDetails')}</h2>
        <span className={styles.Subtitle}>
          {t('dashboard_ParentDetailsSubtitle')}
        </span>
      </div>
    );
  }

  onSendClick() {
    const that = this;
    const { petName, name, email, phoneNumber } = this.state;
    const { t, history, location, __referral } = this.props;
    const {
      featureFlags: { recommendationSMS },
    } = this.context;

    const data = { petName, name, email };
    if (recommendationSMS) {
      data.phoneNumber = phoneNumber;
    }

    const fromReferral = location.state.fromReferral;
    this.setState({ loading: true });

    sendReferral(data, fromReferral)
      .then(() => {
        if (!fromReferral) {
          clearReferral();
        }

        history.push({
          pathname: '/recommendation/success',
          state: {
            user: data,
            referral: fromReferral || __referral,
            from: location.state.from,
            modal: true,
          },
        });
      })
      .catch((err) =>
        that.setState({
          errorMessage: responseFormatter.error(err).message(),
        })
      )
      .finally(() => that.setState({ loading: false }));

    const normReferral = fromReferral || __referral;

    const brand = t('header_title');
    const metadata = { brand };
    handleGtmEvent('purchase', {
      referral: normReferral,
      context: this.context,
      metadata,
    });
  }

  formComponent() {
    const { t } = this.props;
    const {
      petName,
      name,
      email,
      phoneNumber,
      loading,
      errorMessage,
      countryCode: countryCodeFromState,
    } = this.state;
    const {
      featureFlags: { recommendationSMS },
      storeConfig: { countryCode },
    } = this.context;

    if (!countryCodeFromState) this.setupValidator();

    const validateAll = this.validator.validateAll(this.state);
    const showPhoneInvalid =
      recommendationSMS &&
      phoneNumber &&
      !isPhoneNumberValid(phoneNumber, countryCode);
    const showPetNameInvalid = petName && petName.length > 20;
    const isNameFieldInvalid =
      petName.trim().length < 1 || name.trim().length < 1;
    const patientNameAuxText = recommendationSMS
      ? 'dashboard_ParentNameAux'
      : 'dashboard_ParentNameAuxNoSMS';

    return (
      <div className={styles.Form}>
        <Input
          title={t('dashboard_PetName')}
          placeholder={t('dashboard_PetNamePH')}
          className={styles.Input}
          value={petName}
          onChange={(petName) => this.setState({ petName })}
          id="focusedInput"
          required
        />
        {showPetNameInvalid && (
          <span className={styles.AuxiliarError}>
            {t('dashboard_PetNameError')}
          </span>
        )}
        <Input
          title={t('dashboard_ParentName')}
          placeholder={t('dashboard_ParentNamePH')}
          className={styles.Input}
          value={name}
          onChange={(name) => this.setState({ name })}
          required
        />
        <span className={styles.Auxiliar}>{t(patientNameAuxText)}</span>
        <Input
          title={t('dashboard_Email')}
          placeholder={t('dashboard_EmailPH')}
          className={styles.Input}
          value={email}
          onChange={(email) => this.setState({ email })}
          required
        />
        {recommendationSMS && (
          <Input
            title={t('dashboard_Phone')}
            placeholder={t('dashboard_SendRecommendation_PhonePH')}
            className={styles.Input}
            value={phoneNumber}
            onChange={(phoneNumber) => this.setState({ phoneNumber })}
          />
        )}
        {showPhoneInvalid && (
          <span className={styles.AuxiliarError}>
            {t('dashboard_ParentPhoneError')}
          </span>
        )}

        <span className={styles.Disclosure}>
          {keyValidate('dashboard_ConfirmingConsent_patient')
            ? t('dashboard_ConfirmingConsent_patient')
            : t('dashboard_ConfirmingConsent')}
        </span>
        <div className={styles.SubmitArea}>
          <Button
            style={ButtonStyle.Primary}
            className={styles.Button}
            onClick={this.onSendClick}
            loading={loading}
            disabled={!validateAll.isValid || loading || isNameFieldInvalid}
          >
            {t('dashboard_SendRecommendation')}
          </Button>
        </div>
        {errorMessage && (
          <span className={styles.ErrorMessage}>
            {t('dashboard_ErrorMessage')}
          </span>
        )}
      </div>
    );
  }

  render() {
    const { history, location, t } = this.props;
    const componentClasses = classNames(styles.Patient);

    return (
      <FocusTrap
        focusTrapOptions={{
          initialFocus: '#focusedInput',
        }}
      >
        <Modal.Base
          className={componentClasses}
          onClose={() => history.push(location.state.from)}
        >
          <Helmet>
            <title>{t('dashboard_ParentDetails')}</title>
          </Helmet>

          {this.headerComponent()}
          {this.formComponent()}
        </Modal.Base>
      </FocusTrap>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withReferral(withTranslation()(Patient));

Patient.contextType = StoreConfigContext;
